import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Button, Table, Tag, Typography, message } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { playlistApiService } from '../services/playlistApiService';
import { FeedValidation, FeedValidationStatus } from '../types/playlistType';

const { Text } = Typography;

/** How often the list is re-read while a run is pending or running. A run takes ~10s–1min. */
const POLL_INTERVAL_MS = 5000;

const STATUS_TAG: Record<FeedValidationStatus, { color: string; icon: React.ReactNode }> = {
  completed: { color: 'success', icon: <CheckCircleOutlined /> },
  failed: { color: 'error', icon: <CloseCircleOutlined /> },
  running: { color: 'processing', icon: <SyncOutlined spin /> },
  pending: { color: 'default', icon: <ClockCircleOutlined /> },
};

const isActive = (v: FeedValidation) => v.status === 'pending' || v.status === 'running';

interface Props {
  playlistId: number;
  onViewDetail: (validation: FeedValidation) => void;
}

/**
 * Validate Feed runs as an AWS Batch job: POST only queues it and answers `pending`, so the
 * results live in this history list and are polled until the run finishes.
 */
export const FeedValidationHistory: React.FC<Props> = ({ playlistId, onViewDetail }) => {
  const [validations, setValidations] = useState<FeedValidation[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // `silent` keeps the table from flashing its spinner on every poll.
  const fetchHistory = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        setValidations(await playlistApiService.fetchValidationHistory(playlistId));
      } catch {
        if (!silent) message.error('Failed to load validation history');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [playlistId]
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const hasActive = validations.some(isActive);

  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = setInterval(() => fetchHistory(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActive, fetchHistory]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await playlistApiService.submitValidation(playlistId);
      message.success('Validation started — results appear here when it finishes');
    } catch (error) {
      // Someone else (or another tab) already started one; just pick it up.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        message.info('A validation is already in progress');
      } else {
        message.error('Failed to start validation');
      }
    } finally {
      setSubmitting(false);
    }
    fetchHistory();
  };

  const columns = [
    {
      title: 'Requested',
      dataIndex: 'created',
      key: 'created',
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: FeedValidationStatus) => {
        const tag = STATUS_TAG[value] ?? STATUS_TAG.pending;
        return (
          <Tag icon={tag.icon} color={tag.color}>
            {value.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Result',
      key: 'result',
      render: (_: unknown, record: FeedValidation) =>
        record.status === 'completed' ? `${record.valid_count}/${record.total_count} valid` : '-',
    },
    {
      title: 'Invalid',
      dataIndex: 'invalid_count',
      key: 'invalid_count',
      render: (value: number | null, record: FeedValidation) => {
        if (record.status !== 'completed') return '-';
        return <Tag color={value ? 'error' : 'success'}>{value ?? 0}</Tag>;
      },
    },
    {
      title: '',
      key: 'action',
      render: (_: unknown, record: FeedValidation) =>
        record.status === 'completed' ? (
          <Button size="small" onClick={() => onViewDetail(record)}>
            Detail
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ marginTop: '30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Feed Validation</h3>
        <div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchHistory()}
            aria-label="Refresh validation history"
            style={{ marginRight: '8px' }}
          />
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={hasActive}
            style={{ backgroundColor: hasActive ? undefined : '#1890ff' }}
          >
            Validate Feed
          </Button>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', margin: '8px 0 12px' }}>
        Checks that every mp4 in this playlist&apos;s MRSS feeds can actually be downloaded — run it
        before sharing a feed. Archived (Glacier) or deleted files show up as invalid and can be
        recovered from HLS.
      </Text>
      <Table
        dataSource={validations}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />
    </div>
  );
};
